import { sortElementsByLayersWithIndex } from "./CommandContext";
import { svgObjectURL } from "./Elements/factory";
import {
    LayoutElement,
    LayoutState,
    LayoutsState,
    IllustrationElement,
    FrameElement,
    LayoutElementType,
    TextboxElement,
} from "./types";
import { fetchAsString } from "./util/crossplatform";
import { loadFont } from "./util/font";
import { generate } from "./util/guid";
import { generateSVGWithUnknownColors, modifySVGWithElementProperties } from "./util/illustration";

export class ElementNotFoundError extends Error {
    constructor() {
        super("Element not found!");
        Object.setPrototypeOf(this, ElementNotFoundError.prototype);
    }
}

const findElement = <T extends LayoutElement>(id: string, layouts: LayoutState[]): T => {
    const layout = findLayoutForElement(id, layouts);
    const element = layout.elements.find((el) => el.id === id);
    return element as T;
};

const findLayoutForElement = (id: string, layouts: LayoutState[]): LayoutState => {
    const layoutForElement = Object.values(layouts).find((layoutState: LayoutState) => {
        return layoutState.elements.some((el) => el.id === id);
    });
    if (!layoutForElement) {
        throw new ElementNotFoundError();
    }
    return layoutForElement;
};

const updatedLayoutForElement = (element: LayoutElement, state: LayoutState): LayoutState => {
    const updatedElements = [...state.elements.filter((el) => el.id !== element.id), element];
    const layeredElements = sortElementsByLayersWithIndex(updatedElements);
    return {
        layout: state.layout,
        elements: layeredElements,
        modificationID: generate(),
    };
};

const rehydrateSerializedLayout = async (transactionState: LayoutsState) => {
    const layoutNamesWithDupes = [...Object.keys(transactionState.layouts)];
    const layoutNames = [...new Set(layoutNamesWithDupes)];

    for (let i = 0; i < layoutNames.length; i++) {
        const layoutName = layoutNames[i];
        const layout = transactionState.layouts[layoutName];
        await Promise.all(
            layout.elements.map(async (element) => {
                // Rehydrate any illustrations in the layout.
                if (element.type === "illustration") {
                    const illustration = element as IllustrationElement;
                    if (illustration.src && !illustration.svg) {
                        const illustrationBody = await fetchAsString(illustration.src);
                        const init = await generateSVGWithUnknownColors(illustrationBody);
                        illustration.svg = modifySVGWithElementProperties(
                            init.svg,
                            illustration.width,
                            illustration.height,
                            illustration.colors,
                        );
                    }
                } else if (element.type === "frame") {
                    const frame = element as FrameElement;
                    if (frame.pattern?.colors && frame.pattern?.src) {
                        const svgBody = await fetchAsString(frame.pattern?.src);
                        const init = await generateSVGWithUnknownColors(svgBody);
                        frame.pattern.svg = init.svg;
                    }
                }
                // Sanitize values. This likely shouldn't be necessary, but some funky stuff can happen if these values are missing.
                if (isNaN(element.x)) {
                    element.x = 0;
                }
                if (isNaN(element.y)) {
                    element.y = 0;
                }
                if (isNaN(element.width)) {
                    element.width = 0;
                }
                if (isNaN(element.height)) {
                    element.height = 0;
                }
                if (isNaN(element.rotation)) {
                    element.rotation = 0;
                }
            }),
        );
    }

    for (const layoutKey in transactionState.layouts) {
        const illustrations = transactionState.layouts[layoutKey].elements.filter(
            (el) => el.type === LayoutElementType.Illustration,
        );
        for (let i = 0; i < illustrations.length; ++i) {
            const el = illustrations[i] as IllustrationElement;
            if (el.src && el.svg) {
                try {
                    el.cachedObjectURL = await svgObjectURL(el.svg);
                } catch (e) {
                    // If computing the cached object URL fails, we should proceed anyway.
                    console.log(e);
                }
            }
        }
        const fonts = transactionState.layouts[layoutKey].elements.filter(
            (el) => el.type === LayoutElementType.Textbox,
        );
        for (let i = 0; i < fonts.length; ++i) {
            const el = fonts[i] as TextboxElement;
            if (el.fontData?.assetUrl) {
                await loadFont(el.fontData.assetUrl);
            }
        }
    }
};

export { findElement, findLayoutForElement, updatedLayoutForElement, rehydrateSerializedLayout };
